import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { Mail, Lock, ArrowLeft, Shield, Key, MessageSquare, Eye, EyeOff } from 'lucide-react';
import Logo from '../../../components/common/Logo';
import { apiCall } from '../../../services/apiUtil';

/**
 * PASSWORD RESET FLOW (Email Code Method)
 * ========================================
 *
 * FRONTEND (React)                          BACKEND (Fastify)                         AWS
 * ---------------                           -----------------                         ---
 *
 * Step 1: Send Code
 * -----------------
 * User enters email
 *        |
 *        v
 * POST /auth/forgot-password/send-code ──────> auth.js: sendResetCodeOptions
 *   { email }                                         |
 *                                                     v
 *                                              Find user by email (Prisma)
 *                                                     |
 *                                                     v
 *                                              Generate 4-digit code
 *                                              (generateResetCode in auth.js)
 *                                                     |
 *                                                     v
 *                                              Invalidate old codes
 *                                              Store new code in password_reset_codes
 *                                              (expires in 10 min)
 *                                                     |
 *                                                     v
 *                                              notificationService.send({
 *                                                notification_id: 'PASSWORD_RESET',
 *                                                to_email,
 *                                                data: { reset_code, user_first_name, expiry_minutes }
 *                                              })
 *                                                     |
 *                                                     v
 *                                              Lookup notification_configs ──────────> DB
 *                                              (email_subject, email_s3_key)
 *                                                     |
 *                                                     v
 *                                              Fetch template from S3 ────────────────> S3: email_templates/password_reset.html
 *                                                     |
 *                                                     v
 *                                              substituteVariables(template, data)
 *                                              {{reset_code}} -> "1234"
 *                                              {{user_first_name}} -> "John"
 *                                              {{expiry_minutes}} -> "10"
 *                                                     |
 *                                                     v
 *                                              sendViaSES(to_email, subject, body) ───> SES: Send Email
 *        |
 *        v
 * Receive: { message, expires_in_minutes }
 * Show code input form
 *
 *
 * Step 2: Verify Code
 * -------------------
 * User enters 4-digit code
 *        |
 *        v
 * POST /auth/forgot-password/verify-code ────> auth.js: verifyResetCodeOptions
 *   { email, code }                                   |
 *                                                     v
 *                                              Find user by email
 *                                              Find valid code (not used, not expired)
 *                                                     |
 *                                                     v
 *                                              Return: { valid: true }
 *        |
 *        v
 * Show new password form
 *
 *
 * Step 3: Reset Password
 * ----------------------
 * User enters new password
 *        |
 *        v
 * POST /auth/forgot-password/reset-with-code ──> auth.js: resetWithCodeOptions
 *   { email, code, newPassword }                      |
 *                                                     v
 *                                              Validate code again
 *                                              Hash password (bcrypt)
 *                                              Transaction:
 *                                                - Update users.password1
 *                                                - Mark code as used
 *                                                     |
 *                                                     v
 *                                              Return: { message: 'Password reset successfully' }
 *        |
 *        v
 * Show success screen
 * Navigate to sign in
 *
 *
 * DATABASE TABLES
 * ---------------
 * notification_configs:
 *   - notification_id: 'PASSWORD_RESET' (unique key)
 *   - email_subject: 'Kynaix Sports - Your Password Reset Code'
 *   - email_s3_key: 'email_templates/password_reset.html'
 *   - email_enabled, sms_enabled, push_enabled
 *   - max_retries, retry_delay_ms
 *
 * password_reset_codes:
 *   - user_id (FK to users)
 *   - code: '1234'
 *   - expires_at: timestamp (10 min from creation)
 *   - used: boolean
 *
 *
 * S3 TEMPLATE (email_templates/password_reset.html)
 * -------------------------------------------------
 * Variables: {{user_first_name}}, {{reset_code}}, {{expiry_minutes}}
 */

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Steps:
  // 1: Enter Email
  // 2: Choose Method (Security Question or Email Code)
  // 3a: Security Question flow
  // 3b: Email Code flow - Enter code
  // 4: Set New Password (for email code flow)
  // 5: Success
  const [step, setStep] = useState(1);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetMethod, setResetMethod] = useState(null); // 'security' or 'email'
  const [formData, setFormData] = useState({
    email: '',
    securityAnswer: '',
    resetCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [questionNumber, setQuestionNumber] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeExpiryMinutes, setCodeExpiryMinutes] = useState(10);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };

    const missingRequirements = [];
    if (!requirements.minLength) missingRequirements.push('at least 8 characters');
    if (!requirements.hasUppercase) missingRequirements.push('one uppercase letter');
    if (!requirements.hasLowercase) missingRequirements.push('one lowercase letter');
    if (!requirements.hasNumber) missingRequirements.push('one number');
    if (!requirements.hasSpecialChar) missingRequirements.push('one special character');

    return {
      isValid: Object.values(requirements).every(req => req),
      missingRequirements
    };
  };

  // Step 1: Submit email to proceed to method selection
  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setStep(2);
  };

  // Choose security question method
  const handleChooseSecurityQuestion = async () => {
    setIsLoading(true);
    setError('');
    setResetMethod('security');

    try {
      const result = await apiCall('/auth/forgot-password/get-question', {
        method: 'POST',
        body: JSON.stringify({ email: formData.email })
      });
      setSecurityQuestion(result.securityQuestion);
      setQuestionNumber(result.questionNumber);
      setStep('3a');

    } catch (error) {
      console.error('Get security question error:', error);
      setError(error.message || 'Failed to retrieve security question');
    } finally {
      setIsLoading(false);
    }
  };

  // Send reset code to email (used for both initial send and resend)
  const sendResetCode = async (isResend = false) => {
    setIsLoading(true);
    setError('');

    if (!isResend) {
      setResetMethod('email');
    }

    try {
      const result = await apiCall('/auth/forgot-password/send-code', {
        method: 'POST',
        body: JSON.stringify({ email: formData.email })
      });
      setCodeExpiryMinutes(result.expires_in_minutes || 10);

      if (isResend) {
        setFormData(prev => ({ ...prev, resetCode: '' }));
      } else {
        setStep('3b');
      }

    } catch (error) {
      console.error('Send code error:', error);
      setError(error.message || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  // Choose email code method
  const handleChooseEmailCode = () => sendResetCode(false);

  // Verify email code
  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!formData.resetCode || formData.resetCode.length !== 4) {
      setError('Please enter the 4-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await apiCall('/auth/forgot-password/verify-code', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          code: formData.resetCode
        })
      });

      setStep(4);

    } catch (error) {
      console.error('Verify code error:', error);
      setError(error.message || 'Invalid or expired code');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password with email code
  const handleResetWithCode = async (e) => {
    e.preventDefault();

    if (!formData.newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError(`Password must have: ${passwordValidation.missingRequirements.join(', ')}`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await apiCall('/auth/forgot-password/reset-with-code', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          code: formData.resetCode,
          newPassword: formData.newPassword
        })
      });

      setStep(5);

    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password with security question
  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (!formData.securityAnswer) {
      setError('Please answer the security question');
      return;
    }

    if (!formData.newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError(`Password must have: ${passwordValidation.missingRequirements.join(', ')}`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await apiCall('/auth/forgot-password/reset', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          securityAnswer: formData.securityAnswer,
          questionNumber: questionNumber,
          newPassword: formData.newPassword
        })
      });

      setStep(5);

    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend code
  const handleResendCode = () => sendResetCode(true);

  // Success Step
  if (step === 5) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center p-4`}>
        <div className={`${theme.card} rounded-2xl ${theme.shadow} p-8 w-full max-w-md`}>
          <div className="text-center mb-8">
            <Logo size="large" />
            <div className="mt-6 flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h2 className={`text-3xl font-bold ${theme.text} mt-6`}>
              Password Reset Successful
            </h2>
            <p className={`${theme.textSecondary} mt-2`}>
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
          </div>

          <div className="space-y-6">
            <button
              onClick={() => navigate('/signin')}
              className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow`}
            >
              Sign In
            </button>
            <p className={`text-center ${theme.textSecondary}`}>
              <Link
                to="/"
                className={`${theme.link} ${theme.linkHover} font-medium`}
              >
                Back to Home
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background} flex items-center justify-center p-4`}>
      <div className={`${theme.card} rounded-2xl ${theme.shadow} p-8 w-full max-w-md`}>
        <div className="text-center mb-8">
          <Logo size="large" />
          <h2 className={`text-3xl font-bold ${theme.text} mt-6`}>
            {step === 1 && 'Forgot Password'}
            {step === 2 && 'Choose Reset Method'}
            {step === '3a' && 'Security Verification'}
            {step === '3b' && 'Enter Reset Code'}
            {step === 4 && 'Set New Password'}
          </h2>
          <p className={`${theme.textSecondary} mt-2`}>
            {step === 1 && 'Enter your email to reset your password'}
            {step === 2 && 'Select how you want to verify your identity'}
            {step === '3a' && 'Answer your security question to continue'}
            {step === '3b' && `Enter the 4-digit code sent to ${formData.email}`}
            {step === 4 && 'Create your new password'}
          </p>
        </div>

        {/* Step 1: Enter Email */}
        {step === 1 && (
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Email Address</label>
              <div className="relative">
                <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            {error && (
              <div className={`p-3 ${theme.backgroundSecondary} border border-red-400 rounded-lg`}>
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Finding Account...' : 'Continue'}
              </button>
            </div>

            <p className={`text-center ${theme.textSecondary}`}>
              <Link
                to="/signin"
                className={`${theme.link} ${theme.linkHover} font-medium inline-flex items-center`}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Sign In
              </Link>
            </p>
          </form>
        )}

        {/* Step 2: Choose Method */}
        {step === 2 && (
          <div className="space-y-4">
            <button
              onClick={handleChooseEmailCode}
              disabled={isLoading}
              className={`w-full p-4 rounded-lg border-2 ${theme.border} hover:border-blue-500 transition-colors flex items-center gap-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <h3 className={`font-semibold ${theme.text}`}>Email Code</h3>
                <p className={`text-sm ${theme.textSecondary}`}>
                  Receive a 4-digit code via email
                </p>
              </div>
            </button>

            <button
              onClick={handleChooseSecurityQuestion}
              disabled={isLoading}
              className={`w-full p-4 rounded-lg border-2 ${theme.border} hover:border-blue-500 transition-colors flex items-center gap-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full">
                <MessageSquare className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className={`font-semibold ${theme.text}`}>Security Question</h3>
                <p className={`text-sm ${theme.textSecondary}`}>
                  Answer your security question
                </p>
              </div>
            </button>

            {error && (
              <div className={`p-3 ${theme.backgroundSecondary} border border-red-400 rounded-lg`}>
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <p className={`text-center ${theme.textSecondary} mt-4`}>
              <button
                type="button"
                onClick={() => { setStep(1); setError(''); }}
                className={`${theme.link} ${theme.linkHover} font-medium inline-flex items-center`}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Change Email
              </button>
            </p>
          </div>
        )}

        {/* Step 3a: Security Question */}
        {step === '3a' && (
          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                Security Question
              </label>
              <div className={`p-3 rounded-lg ${theme.backgroundSecondary} border ${theme.border} ${theme.text}`}>
                {securityQuestion}
              </div>
            </div>

            <div>
              <label htmlFor="securityAnswer" className={`block text-sm font-medium ${theme.text} mb-2`}>
                Your Answer
              </label>
              <input
                id="securityAnswer"
                name="securityAnswer"
                type="text"
                required
                value={formData.securityAnswer}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                placeholder="Enter your answer"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className={`block text-sm font-medium ${theme.text} mb-2`}>
                New Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-10 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  placeholder="Enter new password"
                />
                <button type="button" onClick={() => setShowNewPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className={`block text-sm font-medium ${theme.text} mb-2`}>
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-10 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  placeholder="Confirm new password"
                />
                <button type="button" onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className={`p-3 ${theme.backgroundSecondary} border border-red-400 rounded-lg`}>
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </div>

            <p className={`text-center ${theme.textSecondary}`}>
              <button
                type="button"
                onClick={() => { setStep(2); setError(''); }}
                className={`${theme.link} ${theme.linkHover} font-medium inline-flex items-center`}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Choose Different Method
              </button>
            </p>
          </form>
        )}

        {/* Step 3b: Enter Email Code */}
        {step === '3b' && (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div>
              <label htmlFor="resetCode" className={`block text-sm font-medium ${theme.text} mb-2`}>
                4-Digit Code
              </label>
              <div className="relative">
                <Key className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  id="resetCode"
                  name="resetCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  required
                  value={formData.resetCode}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-2xl tracking-widest font-mono`}
                  placeholder="0000"
                />
              </div>
              <p className={`text-sm ${theme.textSecondary} mt-2`}>
                Code expires in {codeExpiryMinutes} minutes
              </p>
            </div>

            {error && (
              <div className={`p-3 ${theme.backgroundSecondary} border border-red-400 rounded-lg`}>
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>

            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => { setStep(2); setError(''); }}
                className={`${theme.link} ${theme.linkHover} font-medium inline-flex items-center text-sm`}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading}
                className={`${theme.link} ${theme.linkHover} font-medium text-sm disabled:opacity-50`}
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        {/* Step 4: Set New Password (Email Code Flow) */}
        {step === 4 && (
          <form onSubmit={handleResetWithCode} className="space-y-6">
            <div>
              <label htmlFor="newPassword" className={`block text-sm font-medium ${theme.text} mb-2`}>
                New Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-10 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  placeholder="Enter new password"
                />
                <button type="button" onClick={() => setShowNewPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className={`block text-sm font-medium ${theme.text} mb-2`}>
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-10 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  placeholder="Confirm new password"
                />
                <button type="button" onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className={`p-3 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
              <p className={`text-sm ${theme.textMuted}`}>
                Password must have: 8+ characters, uppercase, lowercase, number, and special character.
              </p>
            </div>

            {error && (
              <div className={`p-3 ${theme.backgroundSecondary} border border-red-400 rounded-lg`}>
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </div>

            <p className={`text-center ${theme.textSecondary}`}>
              <button
                type="button"
                onClick={() => { setStep('3b'); setError(''); }}
                className={`${theme.link} ${theme.linkHover} font-medium inline-flex items-center`}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Code Entry
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
